/* eslint-disable @typescript-eslint/no-explicit-any */

import * as yup from 'yup';
import { Locale } from '../../app/my-account/interfaces/profile.interfaces';
import { interpolateVariables } from '../functions';
import moment from 'moment';
import {
  MAX_DIASTOLIC_BP,
  MAX_SYSTOLIC_BP,
  MIN_DIASTOLIC_BP,
  MIN_SYSTOLIC_BP,
} from '../constants/questionnaire/healthQuestionnaire';

type SchemaFactory<T extends yup.AnySchema> = ((locale: Locale) => T) | T;

const resolveSchema = <T extends yup.AnySchema>(
  factory: SchemaFactory<T>,
  locale: Locale,
): T => (typeof factory === 'function' ? (factory as any)(locale) : factory);

const matchesTrigger = (
  parentValue: unknown,
  triggers: Array<string | number | boolean>,
  mode: 'any' | 'all' = 'any',
): boolean => {
  const toStr = (v: unknown) =>
    typeof v === 'boolean' ? (v ? 'true' : 'false') : String(v);

  const trigSet = new Set(triggers.map(toStr));

  if (Array.isArray(parentValue)) {
    const vals = parentValue.map(toStr);
    return mode === 'all'
      ? triggers.every((t) => vals.includes(toStr(t)))
      : vals.some((v) => trigSet.has(v));
  }

  return trigSet.has(toStr(parentValue));
};

export const arrayRequired = (locale: Locale) =>
  yup.array().min(1, locale.required).required(locale.required);

export const stringRequired = (locale: Locale) =>
  yup.string().required(
    interpolateVariables(locale?.required as unknown as string, {
      label: 'Field',
    }),
  );

export const applyConditionalWhen = <T extends yup.AnySchema>(
  base: T,
  locale: Locale,
  parentName: string | string[],
  triggers: Array<string | number | boolean>,
  thenFactory: SchemaFactory<T>,
  mode: 'any' | 'all' = 'any',
): T =>
  base.when(parentName, {
    is: (val: unknown) => matchesTrigger(val, triggers, mode),
    then: () => resolveSchema(thenFactory, locale),
    otherwise: () => base.notRequired(),
  }) as T;

export const stringRequiredWhen = (
  locale: Locale,
  parentName: string | string[],
  triggers: Array<string | number | boolean>,
  mode: 'any' | 'all' = 'any',
): yup.StringSchema =>
  applyConditionalWhen(
    yup.string(),
    locale,
    parentName,
    triggers,
    stringRequired(locale),
    mode,
  );

const matchesCondition = (value: any, keys: string[]): boolean => {
  if (Array.isArray(value)) {
    return value.some((val) => keys.includes(String(val)));
  }
  return keys.includes(String(value));
};

export const arrayRequiredWhen = (
  locale: Locale,
  dependentKey: string,
  targetValues: string[],
) => {
  return yup.array().when(dependentKey, {
    is: (val: any) => matchesCondition(val, targetValues),
    then: (schema) =>
      schema.typeError(locale?.required).min(1, locale?.required),
    otherwise: () => yup.string().notRequired().nullable(),
  });
};

export const bloodPressureValidation = yup.object({
  idk: yup.boolean().default(false),

  value: yup
    .array()
    .of(yup.string().nullable())
    .length(2)
    .when('idk', {
      is: true,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) =>
        schema
          .test('systolic-required', function (values) {
            const v = Array.isArray(values)
              ? String(values[0] ?? '').trim()
              : '';
            if (!v) {
              return this.createError({
                path: `${this.path}[0]`,
                message: 'Please enter your systolic blood pressure',
              });
            }
            return true;
          })
          .test('systolic-numeric', function (values) {
            const v = Array.isArray(values)
              ? String(values[0] ?? '').trim()
              : '';
            if (!v) return true;
            if (!/^\d+$/.test(v)) {
              return this.createError({
                path: `${this.path}[0]`,
                message: 'Numeric only values are valid',
              });
            }
            return true;
          })
          .test('systolic-range', function (values) {
            const v = Array.isArray(values)
              ? String(values[0] ?? '').trim()
              : '';
            if (!v || !/^\d+$/.test(v)) return true;
            const n = Number(v);
            if (n < MIN_SYSTOLIC_BP || n > MAX_SYSTOLIC_BP) {
              return this.createError({
                path: `${this.path}[0]`,
                message:
                  'Systolic blood pressure value should be in the range between 80 and 180 mmHg',
              });
            }
            return true;
          })

          .test('diastolic-required', function (values) {
            const v = Array.isArray(values)
              ? String(values[1] ?? '').trim()
              : '';
            if (!v) {
              return this.createError({
                path: `${this.path}[1]`,
                message: 'Please enter your Diastolic blood pressure',
              });
            }
            return true;
          })
          .test('diastolic-numeric', function (values) {
            const v = Array.isArray(values)
              ? String(values[1] ?? '').trim()
              : '';
            if (!v) return true;
            if (!/^\d+$/.test(v)) {
              return this.createError({
                path: `${this.path}[1]`,
                message: 'Numeric only values are valid',
              });
            }
            return true;
          })
          .test('diastolic-range', function (values) {
            const v = Array.isArray(values)
              ? String(values[1] ?? '').trim()
              : '';
            if (!v || !/^\d+$/.test(v)) return true;
            const n = Number(v);
            if (n < MIN_DIASTOLIC_BP || n > MAX_DIASTOLIC_BP) {
              return this.createError({
                path: `${this.path}[1]`,
                message:
                  'Diastolic blood pressure value must be in the range between 60 and 120 mmHg',
              });
            }
            return true;
          }),
    }),
});
const INCH_IN_FT = 12;
const MIN_INCHES_TOTAL = 12;
const MAX_INCHES_TOTAL = 10 * INCH_IN_FT;

export const isValidHeightRange = (
  heightFt?: string | number,
  heightIn?: string | number,
): boolean => {
  const ft = parseInt(String(heightFt ?? ''), 10);
  const inch = parseInt(String(heightIn ?? ''), 10);

  if (isNaN(ft) || isNaN(inch)) return false;

  const total = ft * INCH_IN_FT + inch;
  return total >= MIN_INCHES_TOTAL && total <= MAX_INCHES_TOTAL;
};

export const notInFutureDateTest = function (value?: string | number | Date) {
  if (!value) return false;

  const inputDate = new Date(value);
  const today = new Date();

  inputDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return inputDate <= today;
};

export const maxDaysFromTodayTest = (maxDays: number) =>
  function (value: string | undefined) {
    if (!value) return true;

    return moment(value).isSameOrBefore(moment().add(maxDays, 'days'), 'day');
  };

export const minDaysFromTodayTest = (pastDays: number) =>
  function (value: string | undefined) {
    if (!value) return true;

    return moment(value).isSameOrAfter(
      moment().subtract(pastDays, 'days'),
      'day',
    );
  };

export const noFutureDateTest = () =>
  function (value: string | undefined) {
    if (!value) return true;

    return moment(value).isSameOrBefore(moment(), 'day');
  };

export const noPastDateTest = () =>
  function (value: string | undefined) {
    if (!value) return true;

    return moment(value).isSameOrAfter(moment(), 'day');
  };
